'use client'

import { useMemo } from 'react'
import { usePlatformEvents, UsePlatformEventsPropsBase } from './usePlatformEvents'

export interface PlatformEventNotificationMessage {
    keyType: number
    message?: string
    title?: string
    objectIdentifier?: string
    [key: string]: unknown
}

interface UsePlatformEventsKeyListenerProps extends UsePlatformEventsPropsBase {
    /**
     * One or multiple notification keys to react to.
     * If not provided, all notifications will be reacted to.
     */
    notificationKeys?: number | number[]
    callback: (notification: PlatformEventNotificationMessage) => void
}

const parsePlatformEventNotification = (message: string): PlatformEventNotificationMessage | undefined => {
    try {
        const parsedMessage = JSON.parse(message) as {
            data?: PlatformEventNotificationMessage
        } & PlatformEventNotificationMessage

        if (typeof parsedMessage?.data?.keyType === 'number') {
            return parsedMessage.data
        }

        if (typeof parsedMessage?.keyType === 'number') {
            return parsedMessage
        }

        return undefined
    } catch {
        return undefined
    }
}

/**
 * Listen to PlatformEvents and trigger a callback only for selected notification keys. Used to react to specific events, previously handled by SignalR.
 *
 * @example
 * ```ts
 * usePlatformEventsKeyListener({
 *     scope: PlatformEventScopes.Company,
 *     code: companyCode,
 *     notificationKeys: [SignalRNotificationKey.FileUploaded, SignalRNotificationKey.FileDeleted],
 *     callback: () => refetchDocuments(),
 * })
 * ```
 *
 * @param { scope } - Scope in which to listen for events.
 * @param { code } - Identifier inside the scope (for example a company code).
 * @param { notificationKeys } - One or multiple notification keys to react to. If not provided, all notifications will be reacted to.
 * @param { callback } - Invoked when an incoming event contains a matching `keyType`.
 * @param { event } - Optional custom event name; defaults to the SSE "message" event.
 */
export const usePlatformEventsKeyListener = ({
    scope,
    code,
    notificationKeys,
    callback,
    event,
}: UsePlatformEventsKeyListenerProps): void => {
    const notificationKeysSet = useMemo(() => {
        if (!notificationKeys) return new Set()
        return new Set(Array.isArray(notificationKeys) ? notificationKeys : [notificationKeys])
    }, [notificationKeys])

    usePlatformEvents({
        scope,
        code,
        event,
        onEvent: (messageEvent) => {
            const notification = parsePlatformEventNotification(messageEvent.data)
            if (!notification) {
                return
            }

            if (!notificationKeysSet.size || notificationKeysSet.has(notification.keyType)) {
                callback(notification)
            }
        },
    })
}
